import { apiClient, ApiResponse } from './api'
import { API_ENDPOINTS } from '@/constants/api'
import { useSessionStore } from '@/stores/useSessionStore'

// Cart-related types
export interface CartItem {
  id: string
  productId: string
  productName: string
  productSlug: string
  variantId?: string
  variantName?: string
  price: number
  quantity: number
  totalPrice: number
  image?: string
  metadata?: Record<string, any>
}

export interface CartCoupon {
  code: string
  type: 'percentage' | 'fixed'
  value: number
  description: string
  minimumAmount?: number
  expiresAt?: string
}

export interface Cart {
  id: string
  items: CartItem[]
  subtotal: number
  discount: number
  shipping: number
  tax: number
  total: number
  coupon?: CartCoupon
  itemCount: number
  currency: string
  updatedAt: string
}

export interface ShippingEstimate {
  methods: Array<{
    id: string
    name: string
    description: string
    price: number
    estimatedDays: number
  }>
  address: {
    country: string
    state: string
    postalCode: string
  }
}

export interface AddToCartData {
  productId: string
  variantId?: string
  quantity: number
  metadata?: Record<string, any>
}

// Cart management service functions
export const cartService = {
  // Get current cart
  async getCart(): Promise<Cart> {
    const sessionId = useSessionStore.getState().session?.id
    if (!sessionId) {
      throw new Error('No active session')
    }
    const response = await apiClient.get<ApiResponse<Cart>>(API_ENDPOINTS.CART.GET(sessionId))
    return response.data.data
  },

  // Add item to cart
  async addItem(itemData: AddToCartData): Promise<Cart> {
    const response = await apiClient.post<ApiResponse<Cart>>(API_ENDPOINTS.CART.ADD_ITEM, itemData)
    return response.data.data
  },

  // Update cart item quantity
  async updateItem(itemId: string, quantity: number): Promise<Cart> {
    const response = await apiClient.put<ApiResponse<Cart>>(API_ENDPOINTS.CART.UPDATE_ITEM(itemId), { quantity })
    return response.data.data
  },

  // Remove item from cart
  async removeItem(itemId: string): Promise<Cart> {
    const response = await apiClient.delete<ApiResponse<Cart>>(API_ENDPOINTS.CART.REMOVE_ITEM(itemId))
    return response.data.data
  },

  // Clear entire cart
  async clearCart(): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.CART.CLEAR)
  },

  // Apply coupon to cart
  async applyCoupon(couponCode: string): Promise<Cart> {
    const response = await apiClient.post<ApiResponse<Cart>>(API_ENDPOINTS.CART.APPLY_COUPON, { code: couponCode })
    return response.data.data
  },

  // Remove coupon from cart
  async removeCoupon(): Promise<Cart> {
    const response = await apiClient.delete<ApiResponse<Cart>>(API_ENDPOINTS.CART.REMOVE_COUPON)
    return response.data.data
  },

  // Estimate shipping costs
  async estimateShipping(address: {
    country: string
    state: string
    postalCode: string
    city?: string
  }): Promise<ShippingEstimate> {
    const response = await apiClient.post<ApiResponse<ShippingEstimate>>(API_ENDPOINTS.CART.ESTIMATE_SHIPPING, address)
    return response.data.data
  },

  // Add multiple items at once
  async addMultipleItems(items: AddToCartData[]): Promise<Cart> {
    const response = await apiClient.post<ApiResponse<Cart>>(API_ENDPOINTS.CART.ADD_ITEM, { items })
    return response.data.data
  },

  // Update cart item metadata
  async updateItemMetadata(itemId: string, metadata: Record<string, any>): Promise<Cart> {
    const response = await apiClient.patch<ApiResponse<Cart>>(API_ENDPOINTS.CART.UPDATE_ITEM(itemId), { metadata })
    return response.data.data
  },

  // Validate cart before checkout
  async validateCart(): Promise<{
    isValid: boolean
    errors: Array<{
      itemId: string
      type: 'out_of_stock' | 'price_changed' | 'unavailable'
      message: string
    }>
    updatedCart?: Cart
  }> {
    const response = await apiClient.post<
      ApiResponse<{
        isValid: boolean
        errors: Array<{
          itemId: string
          type: 'out_of_stock' | 'price_changed' | 'unavailable'
          message: string
        }>
        updatedCart?: Cart
      }>
    >(`${API_ENDPOINTS.CART.GET}/validate`)
    return response.data.data
  },
}
